import "./App.css";
import React, { useState, useEffect, Suspense } from "react";
import { BrowserRouter as Router, Route, Routes, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { PRIVATE_ROUTES } from "./routes/PrivateRoute";
import { PUBLIC_ROUTES } from "./routes/PublicRoute";
import { ADMIN_ROUTES } from "./routes/AdminRoute";
import { SideBar } from "./components/navigation/sidebar";
import LiberLogin from "./components/pages/liber-login/LiberLogin";
import ChatWidget from "./components/shared/chat-widget/ChatWidget";
import { SubscriptionRouteGuard } from "./components/shared/route-guards/SubscriptionRouteGuard";
import { AdminRouteGuard } from "./components/shared/route-guards/AdminRouteGuard";
import { AdminScopeGuard } from "./components/shared/route-guards/AdminScopeGuard";
import { SubscriptionSessionSync } from "./components/shared/route-guards/SubscriptionSessionSync";
import { PlatformAnnouncementBanner } from "./components/shared/announcements/PlatformAnnouncementBanner";
import { PlatformFeatureFlagsProvider } from "./context/PlatformFeatureFlagsContext";
import { AppDialogProvider } from "./context/AppDialogContext";
import { MaintenanceGate } from "./components/shared/route-guards/MaintenanceGate";
import NotFoundPage from "./components/pages/errors/NotFoundPage";
import { Toaster } from "sonner";

/* Inner component so useLocation works inside <Router> */
function AppRoutes({ isAuthenticated, userData }) {
  const location = useLocation();
  const [showChatWidget, setShowChatWidget] = useState(false);
  const hideWidget = location.pathname.startsWith("/messages");
  const isAdminUser =
    String(userData?.user?.user_type || "").toLowerCase() === "admin";

  useEffect(() => {
    const id = window.requestIdleCallback
      ? requestIdleCallback(() => setShowChatWidget(true), { timeout: 3000 })
      : setTimeout(() => setShowChatWidget(true), 2500);
    return () => (window.cancelIdleCallback ? cancelIdleCallback(id) : clearTimeout(id));
  }, []);

  return (
    <AdminScopeGuard>
      <PlatformAnnouncementBanner />
      <SideBar>
        <SubscriptionSessionSync>
        <Suspense fallback={null}>
          <Routes>
            {PUBLIC_ROUTES.map((route, index) => (
              <Route key={index} path={route.path} element={<route.element />} />
            ))}
            {ADMIN_ROUTES.map((route, index) => {
              const routeElement = isAuthenticated ? (
                <AdminRouteGuard>
                  <route.element />
                </AdminRouteGuard>
              ) : (
                <LiberLogin />
              );
              return <Route key={`admin-${index}`} path={route.path} element={routeElement} />;
            })}
            {PRIVATE_ROUTES.map((route, index) => {
              const routeElement = isAuthenticated ? (
                <SubscriptionRouteGuard>
                  <route.element />
                </SubscriptionRouteGuard>
              ) : (
                <LiberLogin />
              );
              return <Route key={index} path={route.path} element={routeElement} />;
            })}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
        </SubscriptionSessionSync>
      </SideBar>
      {showChatWidget &&
        !hideWidget &&
        !isAdminUser &&
        (userData?.user?.user_type || !userData) && (
        <ChatWidget />
      )}
    </AdminScopeGuard>
  );
}

function App() {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const userData = JSON.parse(localStorage.getItem("userData"));

  return (
    <div className="App">
      <PlatformFeatureFlagsProvider>
        <AppDialogProvider>
          <Toaster position="top-center" richColors closeButton duration={4000} />
          <Router>
            <MaintenanceGate>
              <AppRoutes isAuthenticated={isAuthenticated} userData={userData} />
            </MaintenanceGate>
          </Router>
        </AppDialogProvider>
      </PlatformFeatureFlagsProvider>
    </div>
  );
}

export default App;
